import { useCallback, useEffect } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { DEFAULT_FAVORITE_PRODUCTS } from '../constants';
import { localStorageKeys } from '../lib/localstorageKeys';
import { useProducts } from './useProducts';

export const useFavoriteProducts = () => {
  const { data: products = [] } = useProducts();
  const [value, setFavoriteProducts] = useLocalStorage<Record<string, boolean>>(
    localStorageKeys.favorites,
    {}
  );
  const [initialized, setInitialized] = useLocalStorage<boolean>(
    localStorageKeys.defaultFavoritesInitialized,
    false
  );

  // Initialize default favorites on first visit or if current favorites are
  // from a previous deployment (have favorites, but couldn't find any in
  // products)
  useEffect(() => {
    const resolvedFavorites = Object.keys(value)
      .map((productId) => products.find((p) => p.id === productId)?.ticker)
      .filter((ticker): ticker is string => !!ticker);

    const shouldSetDefaults =
      products.length > 0 &&
      (!initialized || (resolvedFavorites.length === 0 && Object.values(value).length > 0));

    if (shouldSetDefaults) {
      // If user already has valid favorites, just set initialized
      if (resolvedFavorites.length > 0) {
        setInitialized(true);
        return;
      }

      // If user has no favorites, set the default ones
      const defaultFavorites = Object.fromEntries(
        DEFAULT_FAVORITE_PRODUCTS.map((ticker) => products.find((p) => p.ticker === ticker)?.id)
          .filter((id): id is string => !!id)
          .map((id) => [id, true])
      );
      if (Object.keys(defaultFavorites).length > 0) {
        setFavoriteProducts(defaultFavorites);
        setInitialized(true);
      }
    }
  }, [initialized, products, value, setFavoriteProducts, setInitialized]);

  const toggleProductFavorite = useCallback(
    (productId: string) => {
      setFavoriteProducts((prev) => {
        if (prev[productId]) {
          const { [productId]: _, ...rest } = prev;
          return rest;
        } else {
          return { ...prev, [productId]: true };
        }
      });
    },
    [setFavoriteProducts]
  );

  return {
    favoriteProducts: value,
    toggleProductFavorite,
  };
};
